import React from "react";
import { Trans } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Drawer, Divider, Tooltip, List } from "antd";
import { BankOutlined, QuestionCircleOutlined, NotificationOutlined, TagOutlined, ThunderboltOutlined, LoadingOutlined } from "@ant-design/icons";
import { MdAccessTime, MdLanguage, MdPhone, MdForum, MdPeopleAlt, MdArrowBack } from "react-icons/md";
import { Permissions } from "@whatsper/texterchat-common";
import { observer } from "mobx-react";
import classnames from "classnames";
import { PHONE_FORMAT } from "../../constants";
import { getDirection } from "../../helpers";
import useCheckIfMobile from "../../helpers/hooks/useCheckIfMobile";
import { useStore } from "../../store/rootStore";
import PhoneFormatSelect from "./components/PhoneFormatSelect";
import TimeFormatSelect from "./components/TimeFormatSelect";
import LanguageSelect from "./components/LanguageSelect";
import AboutSettings from "./components/menu/About";
import DevelopersSettings from "./components/menu/Developers";
import ChatSettings from "./components/menu/ChatSettings";
import SecuritySettings from "./components/menu/Security";
import { DrawerLevel, ManagerFeature } from "../../store/UIStore";
import { MANAGER_FEATURE_TO_PATH } from "./constants";
import SecondLevelDrawer from "./layout/SecondLevelDrawer";
import ThirdLevelDrawer from "./layout/ThirdLevelDrawer";
import styles from "./SettingsDrawer.module.scss";
const SettingsDrawer = ({ onCloseDrawer, isOpen, className, toggleTemplateDrawer, }) => {
    const history = useHistory();
    const { agentsStore, loginStore, ui, quickRepliesStore } = useStore();
    const secondLevelRef = React.useRef(null);
    const isMobile = useCheckIfMobile();
    const isPermissionsLoaded = !!loginStore.userDetails && agentsStore.rolesFetched;
    const handleCloseFirstLevel = () => {
        ui.closeManagerDrawer(DrawerLevel.FIRST);
        onCloseDrawer();
    };
    const goToAppPath = (path) => {
        ui.closeAllManagerDrawers();
        onCloseDrawer();
        history.push(path, { prevPath: history.location.pathname });
    };
    const openQuickReplies = () => {
        ui.closeAllManagerDrawers();
        onCloseDrawer();
        quickRepliesStore.openSettingsModal();
    };
    const openManager = (feature) => {
        const isAlreadyOpen = ui.isManagerDrawerOpen(DrawerLevel.SECOND) && ui.getManagerFeature() === feature;
        if (isAlreadyOpen) {
            ui.closeAllManagerDrawers();
            return;
        }
        const featurePath = MANAGER_FEATURE_TO_PATH[feature];
        if (featurePath) {
            history.push(`/manage/${featurePath}`);
        }
    };
    const phoneFormatTooltipContent = (React.createElement("div", { className: styles.tooltipContent },
        React.createElement("div", null,
            React.createElement("strong", null,
                React.createElement(Trans, { i18nKey: PHONE_FORMAT.SMART }),
                ":"),
            React.createElement("br", null),
            React.createElement(Trans, { i18nKey: "PF_SMART_ABOUT" })),
        React.createElement("div", null,
            React.createElement("strong", null,
                React.createElement(Trans, { i18nKey: PHONE_FORMAT.INTERNATIONAL }),
                ":"),
            React.createElement("br", null),
            React.createElement(Trans, { i18nKey: "PF_INTERNATIONAL_ABOUT" })),
        React.createElement("div", null,
            React.createElement("strong", null,
                React.createElement(Trans, { i18nKey: PHONE_FORMAT.NATIONAL }),
                ":"),
            React.createElement("br", null),
            React.createElement(Trans, { i18nKey: "PF_NATIONAL_ABOUT" }))));
    const allMenuItems = [
        {
            key: "agents",
            icon: React.createElement(MdPeopleAlt, null),
            label: React.createElement(Trans, { i18nKey: "agentsManager" }),
            onClick: () => openManager(ManagerFeature.AGENTS),
            permission: Permissions.Permission.users_view,
        },
        {
            key: "departments",
            icon: React.createElement(BankOutlined, null),
            label: React.createElement(Trans, { i18nKey: "departmentsManager" }),
            onClick: () => openManager(ManagerFeature.DEPARTMENTS),
            permission: Permissions.Permission.departments_view,
        },
        {
            key: "labels",
            icon: React.createElement(TagOutlined, null),
            label: React.createElement(Trans, { i18nKey: "labelsManager" }),
            onClick: () => openManager(ManagerFeature.LABELS),
            permission: Permissions.Permission.labels_manage,
        },
        // Manager access requires mutation permission
        {
            key: "quickReplies",
            icon: React.createElement(ThunderboltOutlined, null),
            label: React.createElement(Trans, { i18nKey: "quickReplyManager" }),
            onClick: openQuickReplies,
            permission: Permissions.Permission.quick_replies_manage,
        },
        {
            key: "channels",
            icon: React.createElement(NotificationOutlined, null),
            label: React.createElement(Trans, { i18nKey: "channelManager" }),
            onClick: () => openManager(ManagerFeature.CHANNELS),
            permission: Permissions.Permission.channels_view,
        },
        {
            key: "templates",
            icon: React.createElement(MdForum, null),
            label: React.createElement(Trans, { i18nKey: "templateManager" }),
            onClick: toggleTemplateDrawer,
            permission: Permissions.Permission.template_messages_view,
            showOnMobileOnly: true,
        },
    ];
    const menuItems = allMenuItems.filter((item) => {
        // Filter out mobile-only items if not on mobile
        if (item.showOnMobileOnly && !isMobile) {
            return false;
        }
        // Filter by permission if specified
        if (item.permission) {
            return agentsStore.hasPermissions([item.permission]);
        }
        return true;
    });
    const drawerContent = (React.createElement("section", { className: styles.customSection },
        React.createElement("div", { className: styles.settingsGroup },
            React.createElement("div", { className: styles.settingsItem },
                React.createElement("div", { className: styles.settingsItemContent },
                    React.createElement(MdLanguage, { className: styles.settingsIcon }),
                    React.createElement("span", { className: styles.settingsLabel },
                        React.createElement(Trans, { i18nKey: "Languages" }))),
                React.createElement("div", { className: styles.settingsControl },
                    React.createElement(LanguageSelect, null))),
            React.createElement("div", { className: styles.settingsItem },
                React.createElement("div", { className: styles.settingsItemContent },
                    React.createElement(MdAccessTime, { className: styles.settingsIcon }),
                    React.createElement("span", { className: styles.settingsLabel },
                        React.createElement(Trans, { i18nKey: "timeFormat" }))),
                React.createElement("div", { className: styles.settingsControl },
                    React.createElement(TimeFormatSelect, null))),
            React.createElement("div", { className: styles.settingsItem },
                React.createElement("div", { className: styles.settingsItemContent },
                    React.createElement(MdPhone, { className: styles.settingsIcon }),
                    React.createElement(Tooltip, { title: phoneFormatTooltipContent, placement: "top" },
                        React.createElement("div", { className: styles.sectionTitle },
                            React.createElement(Trans, { i18nKey: "phoneFormat" }),
                            React.createElement(QuestionCircleOutlined, null)))),
                React.createElement("div", { className: styles.settingsControl },
                    React.createElement(PhoneFormatSelect, null)))),
        React.createElement(Divider, { className: styles.divider }),
        React.createElement(List, { className: styles.menuList, dataSource: menuItems, bordered: false, loading: {
                spinning: !isPermissionsLoaded,
                indicator: React.createElement(LoadingOutlined, null),
                className: styles.listLoader,
                size: "large",
            }, renderItem: (item) => (React.createElement(List.Item, { className: styles.menuListItem, onClick: item.onClick, style: { borderBottom: 'none' } },
                React.createElement(List.Item.Meta, { avatar: React.createElement("span", { className: styles.menuIcon }, item.icon), title: React.createElement("span", { className: styles.menuText }, item.label) }))) }),
        React.createElement(ChatSettings, { goToAppPath: goToAppPath }),
        React.createElement(SecuritySettings, { goToAppPath: goToAppPath }),
        React.createElement(AboutSettings, null),
        React.createElement(DevelopersSettings, { goToAppPath: goToAppPath })));
    return (React.createElement(React.Fragment, null,
        React.createElement(Drawer, { className: classnames(className, styles.drawer, { [styles.rtl]: getDirection() === "rtl" }), onClose: handleCloseFirstLevel, placement: getDirection() === "rtl" ? "right" : "left", open: isOpen, width: 320, title: React.createElement(Trans, { i18nKey: "Settings" }), bodyStyle: { padding: 0 }, closeIcon: React.createElement(MdArrowBack, { className: styles.closeIcon }) }, drawerContent),
        React.createElement(SecondLevelDrawer, { ref: secondLevelRef, isOpen: ui.isManagerDrawerOpen(DrawerLevel.SECOND), onCloseDrawer: handleCloseFirstLevel }),
        React.createElement(ThirdLevelDrawer, { isOpen: ui.isManagerDrawerOpen(DrawerLevel.THIRD), onCloseDrawer: handleCloseFirstLevel, onListRefresh: () => { var _a; return (_a = secondLevelRef.current) === null || _a === void 0 ? void 0 : _a.refreshList(); } })));
};
export default observer(SettingsDrawer);
